import type { ReactNode } from 'react';

import { Button } from '../Button/Button.tsx';
import { Dialog, DialogActions } from '../Dialog/Dialog.tsx';
import styles from './AlertModal.module.css';

export interface AlertModalProps {
  isOpen: boolean;
  /** Closing without choosing leaves things as they were: the safe outcome. */
  onOpenChange: (open: boolean) => void;
  /** What stops the action, e.g. "Allergy: Examplamycin". */
  title: string;
  /** One sentence of what and why. */
  message: string;
  /** What the stop rests on, e.g. the recorded reaction and who recorded it. */
  evidence?: readonly string[];
  /** The only primary action, and it is the safe one ("Choose another drug"). */
  safeActionLabel: string;
  onSafeAction: () => void;
  /** An optional way to fix the cause (never to override), e.g. "Review allergy". */
  secondaryAction?: { label: string; onAction: () => void };
  /** Anything else under the evidence, e.g. "Provisional rule". */
  children?: ReactNode;
}

/**
 * The blocking alert tier (DESIGN.md §7, CLAUDE.md): a modal with the danger heading and
 * rule, one sentence of what and why, the evidence, and only the safe action as primary.
 * There is no override here; where an override is allowed, use AlertPanel instead.
 */
export function AlertModal({
  isOpen,
  onOpenChange,
  title,
  message,
  evidence = [],
  safeActionLabel,
  onSafeAction,
  secondaryAction,
  children,
}: AlertModalProps) {
  return (
    <Dialog title={title} tone="alert" isOpen={isOpen} onOpenChange={onOpenChange}>
      {({ close }) => (
        <>
          <p className={styles.message}>{message}</p>
          {evidence.length === 0 ? null : (
            <ul className={styles.evidence} aria-label="Evidence">
              {evidence.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          {children}
          <DialogActions>
            {secondaryAction === undefined ? null : (
              <Button
                variant="quiet"
                onPress={() => {
                  secondaryAction.onAction();
                  close();
                }}
              >
                {secondaryAction.label}
              </Button>
            )}
            <Button
              variant="primary"
              autoFocus
              onPress={() => {
                onSafeAction();
                close();
              }}
            >
              {safeActionLabel}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
